import type { ReactNode } from "react";
import { cn } from "@wa/ui";

/**
 * A labelled cell in one of the invoicing forms: the CRM's small-caps label
 * over whatever control the caller passes, with an optional hairline note under
 * it.
 *
 * `min-w-0` sits on the cell here AND on the pill inside `TextField`, which
 * looks like belt and braces and is not. A number input reports an intrinsic
 * width of roughly 218px, and in a grid that width is read off the LABEL rather
 * than off the input - so a track without it refuses to shrink below 218px and
 * paints over the cell next to it. `index.css` already sets `min-width: 0` on
 * `.crm-inp input`, and that alone does nothing.
 */
export function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <span className="crm-lab">{label}</span>
      {children}
      {hint ? <p className="crm-fnote mt-1">{hint}</p> : null}
    </div>
  );
}

/** One line of text in the CRM's sunken pill. `numeric` switches the value to
 *  tabular figures, which is what a payment term or a registry code wants. */
export function TextField({
  label,
  value,
  onChange,
  hint,
  className,
  type,
  min,
  max,
  step,
  inputMode,
  placeholder,
  numeric,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  className?: string;
  type?: string;
  min?: string;
  /** The upper bound, where the server has one. Both ends or the box lies. */
  max?: string;
  step?: string;
  inputMode?: "numeric" | "decimal" | "email" | "tel" | "url";
  placeholder?: string;
  numeric?: boolean;
}) {
  return (
    <Field label={label} hint={hint} className={className}>
      {/* `aria-label` because the caption above is a `span`: `crm-lab` is a
          caption, not a `for`-bound label, so without this a screen reader
          reaches an unnamed box. */}
      <label className="crm-inp min-w-0">
        <input
          type={type ?? "text"}
          min={min}
          max={max}
          step={step}
          inputMode={inputMode}
          value={value}
          placeholder={placeholder}
          aria-label={label}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          className={numeric ? "crm-tnum" : undefined}
        />
      </label>
    </Field>
  );
}

/**
 * The paragraph version of the same field.
 *
 * Deliberately `crm-area` and not a textarea inside `crm-inp`: the pill is a
 * fixed 52px flex row and its child rules only reach `input` and `select`, so a
 * textarea in there keeps its browser border and is squashed to one line.
 */
export function AreaField({
  label,
  value,
  onChange,
  rows,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  className?: string;
}) {
  return (
    <Field label={label} className={className}>
      <textarea
        className="crm-area"
        rows={rows ?? 3}
        value={value}
        placeholder={placeholder}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}
